import type { AxiosInstance } from "axios";
import type {
  ICurrentJob,
  IFactoryFunctionRequest,
  IFactoryResponse,
  IPaginated,
  IResource,
  IWorkPermit,
} from "@bc-library/interface";
import { EMethod } from "@bc-library/interface";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import useAxios from "@/services/api/useAxios";
import { useUserStore } from "@/config/store/userStore";
import type {
  ICandidateApplication,
  TDocumentTrackingExportPayload,
  TDocumentTrackingQuery,
  TDocumentTrackingResponse,
} from "@/services/resource/interfaces";
import degreesService from "@/services/resource/degreeHttp.service";
import skillsService from "@/services/resource/skillHttp.service";
import {
  afterReceiving,
  afterReceivingWorkPermits,
  beforeSending,
  beforeSendingCandidate,
  beforeSendingWorkPermits,
  JobToUTC,
  beforeSendingCandidateToResource,
  beforeSendingJobField,
} from "@/services/resource/hooks";

let userStore;
const getConnectedUser = () => {
  if (!userStore) userStore = useUserStore();
  return userStore;
};

const resource = "resources";

// const getConnectedResource = (axios: AxiosInstance) => (): AxiosPromise => {
//     return axios.get(`${BASE_URL}/${API_MAIN_VERSION}/${resource}/me`, {headers: {authorization: `Bearer ${userStore.getToken}`}});
// }

const createResource =
  (axios: IFactoryFunctionRequest<IResource, IResource>) => (resourceToSend: any) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
      data: resourceToSend,
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceiving,
    });
  };

const showBirthdays = (axios: IFactoryFunctionRequest<IResource[], IResource[]>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/showBirthdays`,
    afterReceivingHook: afterReceiving,
  });
};

interface IResourceListQuery {
  page: number;
  limit: number;
  display?: string;
  settings?: {
    recruitmentDate?: Date;
    recruitmentDateEnd?: Date;
    departureDate?: Date;
    departureDateEnd?: Date;
    jobs?: string[];
    teams?: string[];
    offices?: string[];
    groups?: string[];
  };
}

interface IHrFilterQuery {
  display: string;
  limit: number;
  page: number;
  resourceType: number;
  jobs: [string];
  jobCategories: string[];
  teams: [string];
  offices: [string];
  userGroups: [string];
  contractTypes: [string];
  /** JSON `[{ skillId, value }]` */
  skills?: string;
  recruitmentDate: Date;
  recruitmentDateEnd: Date;
  departureDate: Date;
  departureDateEnd: Date;
  resources: [string];
}
const hrBaseList =
  (axios: IFactoryFunctionRequest<IPaginated<IResource>, any>) => (query: IHrFilterQuery) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/employeeList/paginated`,
      query: { ...query },
      afterReceivingHook: afterReceiving,
    });
  };

const hrBaseAdjacentIds =
  (axios: IFactoryFunctionRequest<{ prevId: string | null; nextId: string | null }, any>) =>
  (resourceId: string, query: Partial<IHrFilterQuery> = {}) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/employeeList/adjacentIds/${resourceId}`,
      query: { ...query },
    });
  };

const alumniBaseList =
  (axios: IFactoryFunctionRequest<IPaginated<IResource>, any>) =>
  (query: { page: number; limit: number; display?: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/alumniList/paginated`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };

const documentTracking =
  (axios: IFactoryFunctionRequest<TDocumentTrackingResponse, any>) =>
  (query: TDocumentTrackingQuery) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/documentTracking`,
      query,
      immediate: false,
      afterReceivingHook: afterReceiving,
    });
  };

/** A zip (files, plus the summary unless `recap` is false) or the summary workbook alone. */
const exportDocumentTracking =
  (axios: IFactoryFunctionRequest<Blob, TDocumentTrackingExportPayload>) => () => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/documentTracking/export`,
      responseType: "blob",
      immediate: false,
    });
  };

const getHrResourceProfile =
  (axios: IFactoryFunctionRequest<IResource, any>) => (resourceId: string) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/hrProfile/${resourceId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const getAllLight =
  (axios: IFactoryFunctionRequest<Array<IResource>, any>) =>
  ({ firmId }: { firmId: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/light/${firmId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const countTeams =
  (axios: IFactoryFunctionRequest<Array<{ _id: string; count: number }>, undefined>) =>
  ({ firmId }: { firmId: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/countTeams/${firmId}`,
    });
  };

const showPaginatedLight =
  (axios: IFactoryFunctionRequest<Array<IResource>, any>) =>
  ({
    page = 1,
    search = "",
    jobs = [],
    jobCategories = [],
    resources = [],
    limit = 30,
    skills = "[]",
    offices = [],
    immediate = true,
    ignoreRestriction = false,
  }: {
    page?: number;
    search?: string;
    jobs?: string[];
    jobCategories?: string[];
    resources?: string[];
    limit?: number;
    skills: string;
    offices?: string[];
    immediate: boolean;
    ignoreRestriction?: boolean;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/paginated/light`,
      query: {
        page,
        search,
        jobs,
        jobCategories,
        resources,
        limit,
        skills,
        offices,
        evaluatorSearch: ignoreRestriction,
      },
      immediate,
      afterReceivingHook: afterReceiving,
    });
  };

const showPaginatedTrainingResources =
  (axios: IFactoryFunctionRequest<Array<IResource>, any>) =>
  ({
    page = 1,
    search = "",
    jobs = [],
    jobCategories = [],
    limit = 30,
    skills = "[]",
    offices = [],
    immediate = true,
  }: {
    page?: number;
    search?: string;
    jobs?: string[];
    jobCategories?: string[];
    limit?: number;
    skills: string;
    offices?: string[];
    immediate: boolean;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/paginated/training`,
      query: { page, search, jobs, jobCategories, limit, skills, offices },
      immediate,
      afterReceivingHook: afterReceiving,
    });
  };

const showPaginatedLeaveValidator =
  (axios: IFactoryFunctionRequest<Array<IResource>, any>) =>
  ({
    page = 1,
    search = "",
    jobs = [],
    limit = 30,
    leaveTypeId,
    startDate,
    endDate,
  }: {
    page?: number;
    search?: string;
    jobs?: string[];
    limit?: number;
    leaveTypeId: string;
    startDate: Date;
    endDate: Date;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/leave/validators/${leaveTypeId}/${startDate}/${endDate}`,
      query: { page, search, jobs, limit },
      afterReceivingHook: afterReceiving,
    });
  };

const updateResource =
  (axios: IFactoryFunctionRequest<IResource, IResource>) =>
  (resourceId: string, resourceToSend: any) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${resourceId}`,
      data: resourceToSend,
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceiving,
    });
  };

const toJsonSafeCandidateApplication = (candidateApplication: ICandidateApplication) => {
  const { candidateFiles: _candidateFiles, documents = [], ...rest } = candidateApplication;
  return beforeSendingCandidate({
    ...rest,
    documents: documents.map((doc) => ({
      fileName: doc.fileName,
      fileType: doc.fileType,
      fileSize: doc.fileSize,
      fileDescription: doc.fileDescription || doc.fileName,
    })),
  });
};

const createCandidateApplicationFormData = (candidateApplication: ICandidateApplication) => {
  const formData = new FormData();
  formData.append("payload", JSON.stringify(toJsonSafeCandidateApplication(candidateApplication)));

  const cv = candidateApplication.candidateFiles?.[0]?.rawFile;
  const coverLetter = candidateApplication.candidateFiles?.[1]?.rawFile;
  if (cv) formData.append("cv", cv, cv.name);
  if (coverLetter) formData.append("coverLetter", coverLetter, coverLetter.name);
  for (const document of candidateApplication.documents || []) {
    if (document.rawFile) formData.append("documents", document.rawFile, document.rawFile.name);
  }
  return formData;
};

const createCandidateApplication =
  (axios: IFactoryFunctionRequest<undefined, FormData>) =>
  (candidateApplication: ICandidateApplication) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateApplication`,
      data: createCandidateApplicationFormData(candidateApplication),
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const deleteCandidate =
  (axios: IFactoryFunctionRequest<IResource, any>) => (resourceId: string) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidate/${resourceId}`,
    });
  };

const getConnectedResource =
  (axios: IFactoryFunctionRequest<IResource, any>) => (): IFactoryResponse<IResource, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/connectedUser`,
      afterReceivingHook: afterReceiving,
    });
  };

const getOne = (axios: IFactoryFunctionRequest<IResource, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/profile/${resourceId}`,
    afterReceivingHook: afterReceiving,
  });
};
const getOneToStaff = (axios: IFactoryFunctionRequest<IResource, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/project/getOne/toStaff/${resourceId}`,
    afterReceivingHook: afterReceiving,
  });
};
const getInternalInterval =
  (axios: IFactoryFunctionRequest<Array<IResource>, any>) =>
  ({
    search,
    startDate,
    endDate,
    jobs = [],
    jobCategories = [],
    offices = [],
    skills = [],
  }: {
    search?: string;
    startDate: Date;
    endDate: Date;
    jobs?: string[];
    jobCategories?: string[];
    offices?: string[];
    skills?: any[];
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/planning/interval/${startDate}/${endDate}`,
      query: { jobs, jobCategories, offices, search, skills: JSON.stringify(skills) },
      afterReceivingHook: afterReceiving,
    });
  };

const getSuperAdminResourceList =
  (axios: IFactoryFunctionRequest<IPaginated<IResource>, any>) =>
  (query: { page: number; limit: number; display?: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdminList`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };

const filter =
  (axios: IFactoryFunctionRequest<IPaginated<IResource>, any>) =>
  (query: {
    page: number;
    limit: number;
    display?: string;
    project?: [string];
    firmId?: string;
    onlyRecruiter?: boolean;
    evaluatorSearch?: boolean;
    includeFutureJobs?: boolean;
    country?: string;
  }) => {
    if (!query.project) delete query.project;
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };

const getResourceCountByProfileId =
  (axios: IFactoryFunctionRequest<{ count: number }, any>) => (profileId) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/profile/${profileId}/count`,
      afterReceivingHook: afterReceiving,
    });
  };

const getSmallestResourceInfo =
  (
    axios: IFactoryFunctionRequest<{ firstname: string; lastname: string; picture: string }[], any>,
  ) =>
  (resourceIds: string[]) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/smallestResources`,
      data: { resourceIds },
      afterReceivingHook: afterReceiving,
    });
  };

const getResourceBulkLeaveCreate =
  (
    axios: IFactoryFunctionRequest<
      IPaginated<{ _id: string; firstname: string; lastname: string; picture: string }>,
      any
    >,
  ) =>
  (query) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/leave/bulkCreate`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };
const updateResourcePicture =
  (axios: IFactoryFunctionRequest<{ picture: string }, any>) =>
  (resourceId: string, { base64 }: { base64: string }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/picture/${resourceId}`,
      data: { data: base64 },
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceiving,
    });
  };

const retreiveInterviewAttendee =
  (
    axios: IFactoryFunctionRequest<
      {
        _id: string;
        firstname: string;
        lastname: string;
        userId: string;
        currentJob: any;
        user: { _id: string; email: string };
      },
      any
    >,
  ) =>
  (resourceId: string) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/retreiveInterviewAttendee/${resourceId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const exportResourcesToExel = (axios: IFactoryFunctionRequest<IResource, any>) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  });
};
const exportResourcesRetentionToExcel =
  (axios: IFactoryFunctionRequest<IResource, any>) => (query) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export/retention`,
      query,
      immediate: false,
    });
  };
const exportResourcesPresenceToExcel =
  (axios: IFactoryFunctionRequest<IResource, any>) => (query) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export/presence`,
      query,
      immediate: false,
    });
  };

const candidateToResource = (axios: IFactoryFunctionRequest<IResource, any>) => (body) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/candidateToResource`,
    data: body,
    beforeSendingHook: beforeSendingCandidateToResource,
  });
};
const partialUpdate = (axios: IFactoryFunctionRequest<IResource, any>) => (body) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${body._id}`,
    data: body,
    beforeSendingHook: beforeSending,
  });
};

const searchIfResourceExist =
  (axios: IFactoryFunctionRequest<IResource[], any>) =>
  ({ firstname, lastname, email }: { firstname: string; lastname: string; email: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/searchIfResourceExist`,
      query: {
        firstname,
        lastname,
        email,
      },
    });
  };

const getManagedResources =
  (axios: IFactoryFunctionRequest<IResource, any>) => (resourceId: string) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/managedResources/${resourceId}`,
      afterReceivingHook: (data) => {
        return data.map((resource: IResource) => {
          return {
            ...resource,
            jobs: JobToUTC(resource.jobs),
          };
        });
      },
    });
  };

// router.patch('/:resourceId/nationalities', auth.isAuth(), controller.updateNationalities);
const updateNationalities =
  (axios: IFactoryFunctionRequest<IResource, any>) =>
  (
    resourceId: string,
    { nationalities, nationality }: { nationalities: any[]; nationality: string },
  ) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/nationalities`,
      data: { nationalities, nationality },
    });
  };

const updateWorkPermits =
  (
    axios: IFactoryFunctionRequest<{ workPermits: IWorkPermit[] }, { workPermits: IWorkPermit[] }>,
  ) =>
  (resourceId: string, { workPermits }: { workPermits: IWorkPermit[] }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/workPermits`,
      data: { workPermits },
      beforeSendingHook: beforeSendingWorkPermits,
      afterReceivingHook: afterReceivingWorkPermits,
    });
  };

const uploadWorkPermitDocument =
  (axios: IFactoryFunctionRequest<{ workPermits: IWorkPermit[] }, any>) =>
  (
    resourceId: string,
    workPermitId: string,
    document: {
      base64: string;
      fileDescription?: string;
      originalFileName?: string;
      fileName?: string;
      fileType?: string;
    },
  ) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/workPermits/${workPermitId}/document`,
      data: { document },
      afterReceivingHook: afterReceivingWorkPermits,
    });
  };

/**
 * use v1 cause use cron futur job in other country or other office
 * @param resourceId
 * @param alumniPeriod
 * @param isDepartureReplace
 * @returns IRessource
 */
const pushJob =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ resourceId, job }: { resourceId: string; job: ICurrentJob }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${resourceId}/addJob`,
      data: {
        job: job,
      },
      beforeSendingHook: beforeSendingJobField,
    });
  };

/**
 *
 * @param resourceId
 * @param alumniPeriod
 * @param isDepartureReplace
 * @returns ICurrentJob data.alumniPeriod
 */
const pushAlumniPeriod =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    resourceId,
    alumniPeriod,
    isDepartureReplace = false,
  }: {
    resourceId: string;
    alumniPeriod: ICurrentJob;
    isDepartureReplace?: boolean;
  }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/alumniPeriod`,
      data: {
        alumniPeriod,
        isDepartureReplace,
      },
    });
  };

const getCurrentJob = (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/currentJob`,
  });
};

const updateJob =
  (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string, jobToUpdate: ICurrentJob) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/job/${jobToUpdate._id}`,
      data: { jobToUpdate },
      beforeSendingHook: JobToUTC,
    });
  };

/**
 * Delete Resource job AND alumni period
 * @param axios
 * @returns
 */
const deleteJob =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ id, resourceId }: { id: string; resourceId: string }) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/job/${id}`,
    });
  };

const sendInvitations =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ resourceIds, resetPassword = true }: { resourceIds: string[]; resetPassword?: boolean }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/sendInvitations`,
      data: { resourceIds, resetPassword },
    });
  };

const deleteResources = (axios: IFactoryFunctionRequest<any, any>) => (resourceIds: string[]) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/deleteResources`,
    data: { resourceIds },
  });
};

/**
 * Export HR Base to Excel file (use post cause of the size of parameters)
 * @param axios
 * @returns
 */
const exportHrBase =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ filters, fields } = { filters: {}, fields: [] }) => {
    // filters = filters || {}
    // fields = fields || []
    const data = { filters, fields };
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export/hrBase`,
      data,
    });
  };

const getEvaluationResourceSkill =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ resourceId }: { resourceId: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluationResourceSkill/${resourceId}`,
    });
  };

const cancelHiring =
  (axios: IFactoryFunctionRequest<any, any>) => (candidateId: string, processId: string) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/cancelHired/${candidateId}/process/${processId}`,
    });
  };

/**
 * Get all resources recruitment date and departure date for calendar between two dates
 * @param {startDate: Date, endDate: Date} query
 * @returns {IResource[]}
 */
const calendarResource =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (query): IFactoryResponse<any, any> => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/calendarResource`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };

const getComments = (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/comments`,
  });
};

const addCommentToResource =
  (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string, comment) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/comments`,
      data: comment,
    });
  };

const searchSimilarCandidatesForResource =
  (axios: IFactoryFunctionRequest<any, any>) => (processId: string) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/similarCandidate/${processId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const bulkPromote =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (data: { date: string; promotions: Array<{ resourceId: string; positionId: string }> }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/promote`,
      data,
    });
  };

const globalSearch =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (searchString = "") => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/search`,
      query: { searchString },
      immediate: false,
    });
  };

const globalSearchAlumni =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (searchString = "") => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/alumniList/paginated`,
      query: { display: searchString, page: 0, limit: 5 },
      immediate: false,
      afterReceivingHook: afterReceiving,
    });
  };

const addDocuments =
  (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string, documents: Array<any>) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents`,
      data: { documents },
    });
  };

const deleteDocument =
  (axios: IFactoryFunctionRequest<any, any>) => (resourceId: string, documentId: string) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents/${documentId}`,
    });
  };

const toggleDocumentPublicStatus =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (resourceId: string, documentId: string, isPublic: boolean) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents/${documentId}/togglePublicStatus`,
      data: { isPublic },
    });
  };

const moveDocument =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (resourceId: string, fromIndex: number, toIndex: number) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents/move`,
      data: { fromIndex, toIndex },
    });
  };

const moveDocumentById =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (resourceId: string, documentId: string, toIndex: number) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents/move`,
      data: { documentId, toIndex },
    });
  };

const placeDocuments =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (
    resourceId: string,
    payload: {
      documentIds: string[];
      targetOfficeId?: string | null;
      targetFolderId?: string | null;
      targetIndex?: number | null;
    },
  ) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents/place`,
      data: payload,
    });
  };

const fulfillExpectedDocument =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (resourceId: string, documentId: string, documentDefinitionKey: string | null) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/documents/${documentId}/expectedDocument`,
      data: { documentDefinitionKey },
    });
  };

const bulkUpdate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (
    updates: {
      resourceId: string;
      fields?: Record<string, any>;
      jobUpdates?: { jobId: string; fields: Record<string, any> }[];
    }[],
  ) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-update`,
      data: { updates },
    });
  };

const bulkUpdateTemplate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (opts: { includeCurrentData?: boolean } = {}) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-update/template`,
      data: opts,
    });
  };

const bulkUpdateValidate = (axios: IFactoryFunctionRequest<any, any>) => (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-update/validate`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

const bulkCreateTemplate = (axios: IFactoryFunctionRequest<any, any>) => (firmId?: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/template`,
    query: firmId ? { firmId } : undefined,
  });
};

const bulkCreateValidate =
  (axios: IFactoryFunctionRequest<any, any>) => (file: File, firmId?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/validate`,
      query: firmId ? { firmId } : undefined,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const bulkCreate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (
    rows: { data: Record<string, any>; resolvedData?: Record<string, any> }[],
    extraJobRows?: { data: Record<string, any>; isValid: boolean; email: string }[],
    schoolRows?: { data: Record<string, any>; isValid: boolean; email: string }[],
    firmId?: string,
    integrationExclusions?: string[],
  ) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create`,
      data: {
        rows,
        extraJobRows: extraJobRows || [],
        schoolRows: schoolRows || [],
        ...(firmId ? { firmId } : {}),
        ...(integrationExclusions ? { integrationExclusions } : {}),
      },
    });
  };

export default (axios: AxiosInstance) => ({
  // getConnectedResource: getConnectedResource(axios),
  createResource: createResource(useAxios(axios)),
  getAllLight: getAllLight(useAxios(axios)),
  countTeams: countTeams(useAxios(axios)),
  showPaginatedLight: showPaginatedLight(useAxios(axios)),
  showPaginatedTrainingResources: showPaginatedTrainingResources(useAxios(axios)),
  getHrResourceProfile: getHrResourceProfile(useAxios(axios)),
  hrBaseList: hrBaseList(useAxios(axios)),
  hrBaseAdjacentIds: hrBaseAdjacentIds(useAxios(axios)),
  alumniBaseList: alumniBaseList(useAxios(axios)),
  documentTracking: documentTracking(useAxios(axios)),
  exportDocumentTracking: exportDocumentTracking(useAxios(axios)),
  updateResource: updateResource(useAxios(axios)),
  partialUpdate: partialUpdate(useAxios(axios)),
  createCandidateApplication: createCandidateApplication(useAxios(axios)),
  getConnectedResource: getConnectedResource(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  getOneToStaff: getOneToStaff(useAxios(axios)),
  getInternalInterval: getInternalInterval(useAxios(axios)),
  getResourceCountByProfileId: getResourceCountByProfileId(useAxios(axios)),
  showPaginatedLeaveValidator: showPaginatedLeaveValidator(useAxios(axios)),
  getSuperAdminResourceList: getSuperAdminResourceList(useAxios(axios)),
  getSmallestResourceInfo: getSmallestResourceInfo(useAxios(axios)),
  filter: filter(useAxios(axios)),
  getResourceBulkLeaveCreate: getResourceBulkLeaveCreate(useAxios(axios)),
  updateResourcePicture: updateResourcePicture(useAxios(axios)),
  retreiveInterviewAttendee: retreiveInterviewAttendee(useAxios(axios)),
  exportResourcesToExel: exportResourcesToExel(useAxios(axios)),
  candidateToResource: candidateToResource(useAxios(axios)),
  deleteCandidate: deleteCandidate(useAxios(axios)),
  searchIfResourceExist: searchIfResourceExist(useAxios(axios)),
  exportResourcesRetentionToExcel: exportResourcesRetentionToExcel(useAxios(axios)),
  getManagedResources: getManagedResources(useAxios(axios)),
  updateNationalities: updateNationalities(useAxios(axios)),
  updateWorkPermits: updateWorkPermits(useAxios(axios)),
  uploadWorkPermitDocument: uploadWorkPermitDocument(useAxios(axios)),
  sendInvitations: sendInvitations(useAxios(axios)),
  pushAlumniPeriod: pushAlumniPeriod(useAxios(axios)),
  getCurrentJob: getCurrentJob(useAxios(axios)),
  updateJob: updateJob(useAxios(axios)),
  pushJob: pushJob(useAxios(axios)),
  deleteJob: deleteJob(useAxios(axios)),
  exportHrBase: exportHrBase(useAxios(axios)),
  getEvaluationResourceSkill: getEvaluationResourceSkill(useAxios(axios)),
  exportResourcesPresenceToExcel: exportResourcesPresenceToExcel(useAxios(axios)),
  cancelHiring: cancelHiring(useAxios(axios)),
  calendarResource: calendarResource(useAxios(axios)),
  deleteResources: deleteResources(useAxios(axios)),
  getComments: getComments(useAxios(axios)),
  addCommentToResource: addCommentToResource(useAxios(axios)),
  degrees: degreesService(axios),
  skills: skillsService(axios),
  searchSimilarCandidatesForResource: searchSimilarCandidatesForResource(useAxios(axios)),
  globalSearch: globalSearch(useAxios(axios)),
  globalSearchAlumni: globalSearchAlumni(useAxios(axios)),
  showBirthdays: showBirthdays(useAxios(axios)),
  bulkPromote: bulkPromote(useAxios(axios)),
  addDocuments: addDocuments(useAxios(axios)),
  deleteDocument: deleteDocument(useAxios(axios)),
  toggleDocumentPublicStatus: toggleDocumentPublicStatus(useAxios(axios)),
  moveDocument: moveDocument(useAxios(axios)),
  moveDocumentById: moveDocumentById(useAxios(axios)),
  placeDocuments: placeDocuments(useAxios(axios)),
  fulfillExpectedDocument: fulfillExpectedDocument(useAxios(axios)),
  bulkUpdate: bulkUpdate(useAxios(axios)),
  bulkUpdateTemplate: bulkUpdateTemplate(useAxios(axios)),
  bulkUpdateValidate: bulkUpdateValidate(useAxios(axios)),
  bulkCreateTemplate: bulkCreateTemplate(useAxios(axios)),
  bulkCreateValidate: bulkCreateValidate(useAxios(axios)),
  bulkCreate: bulkCreate(useAxios(axios)),
});
